import { useQueryClient } from '@tanstack/react-query'
import type { Editor } from '@tiptap/core'
import { EditorState } from '@tiptap/pm/state'
import type {
  SnapshotVersion,
  CollabOnUpdateProps,
} from '@tiptap-pro/extension-snapshot'
import { TiptapCollabProvider } from '@tiptap-pro/provider'
import classnames from 'classnames'
import debounce from 'lodash/debounce'
import {
  VFC,
  useState,
  Fragment,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react'
import { useBeforeUnload } from 'react-router-dom'
import { Doc as YDoc } from 'yjs'
import { useReadContentById } from '@opallabs/api'
import { getBackboneWindow } from '@opallabs/brown-dwarf'
import { GemColors } from '@opallabs/gem'
import { usePostSaveCallback, useTheme } from '@opallabs/hooks'
import { TextEditor, TextEditorAsset } from '../TextEditor'
import { AssetModal } from '../TextEditor/components/AssetModal'
import { ReadMoreOverlay } from '../TextEditor/components/ReadMoreOverlay'
import { ShortcutModal } from '../TextEditor/components/ShortcutModal'
import { TextEditorContent } from '../TextEditor/content'
import {
  DocumentProvider,
  mergeExtensions,
  useDocumentAssetUpload,
} from '../TextEditor/hooks'
import {
  type RichTextDocument,
  useRichText,
  useRichTextAssets,
} from '../TextEditor/queries'
import { queryKeys } from '../TextEditor/queries/queryKeys'
import { PENDING_COLLABORATION_TOKEN } from '../TextEditorTokenProvider'
import { createMentionListRenderer } from '../Thread/MentionList'
import { useRegisterTiptapEditor } from '../tiptap-editor-registry'
import { AIReviewBanner } from './components/AIReviewBanner'
import { ConnectionBanner } from './components/ConnectionBanner'
import { FullControlBar } from './components/FullControlBar'
import { FullscreenOverlay } from './components/FullscreenOverlay'
import { FullscreenToggle } from './components/FullscreenToggle'
import { OpenVersionsButton } from './components/OpenVersionsButton'
import { PlaceholderDocument } from './components/PlaceholderDocument'
import { SelectionMenu } from './components/SelectionMenu'
import { ThreadPopover } from './components/ThreadPopover'
import { VersioningModal } from './components/VersioningModal'
import {
  useControlBarFocus,
  useContentSyncStatus,
  useThreadNavigation,
} from './hooks'
import { THREAD_PARAM_KEY } from './hooks/useThreadNavigation'
import { useThreads } from './hooks/useThreads'
import * as styles from './styles'
import {
  type CollaborationTextEditorProps,
  type EditorSaveState,
} from './types'
import { getCursorColor, getDefaultProviderConfig, getMentions } from './utils'

const selectBody = (document?: RichTextDocument) =>
  document?.data.attributes.body ?? null

// Updates current URL where the editor is when a thread is opened or closed
function setThreadParam(threadId: string | null) {
  const baseUrl = window.top?.location.href || window.location.href
  const currentUrl = new URL(baseUrl)

  if (threadId) {
    currentUrl.searchParams.set(THREAD_PARAM_KEY, threadId)
  } else {
    currentUrl.searchParams.delete(THREAD_PARAM_KEY)
  }

  window.top?.history.replaceState({ threadId }, '', currentUrl.toString())
}

export const CollaborationTextEditor: VFC<CollaborationTextEditorProps> = ({
  alwaysShowVersionHistoryControl = false,
  alwaysShowControlBar = false,
  autofocus = false,
  className,
  controlBarPlacement = 'bottom',
  externalAccessToken,
  disableDoneButton = false,
  disableFullScreenAndCenterControls = false,
  disableFullScreenOverlay = false,
  disableLastSavedTime = false,
  disableReadMoreOverlay = false,
  disableViewModeSelector = false,
  documentId,
  hasError = false,
  isFullScreen = false,
  onPickAssets,
  onSaveContent,
  onToggleFullScreen,
  placeholder = 'Begin writing...',
  readOnly = false,
  showVersionHistoryControlOnHover = false,
  user,
  useVersioning = false,
  resource,
  selectionMenuAnchor,
}) => {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const [editor, setEditor] = useState<Editor | null>(null)
  const [provider, setProvider] = useState<TiptapCollabProvider>()
  const yDoc = useRef<YDoc | null>(null)
  const { controlBarFocusRef, handleFocusControlBar } = useControlBarFocus()
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [showVersions, setShowVersions] = useState(false)
  const [saveState, setSaveState] = useState<EditorSaveState>()
  const [versions, setVersions] = useState<SnapshotVersion[]>([])
  const [expandedAsset, setExpandedAsset] = useState<TextEditorAsset | null>(
    null
  )
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)

  // Registers the editor instance with registry, but only once the collaboration
  // document has arrived — the same signal the shimmer waits on. Before that the
  // editor holds an empty placeholder doc, and a consumer that reads it (the Gem
  // copilot) would take a document with content to be blank, then act on that
  // reading.
  useRegisterTiptapEditor(
    documentId,
    initialLoadComplete ? editor : null,
    resource,
    provider
  )

  const { data: assetStore } = useRichTextAssets({ documentId })
  const { data: preSyncBody, isRefetching: isRefetchingPreSyncBody } =
    useRichText({ documentId, select: selectBody })

  const handleUploadAssets = useDocumentAssetUpload({
    documentId,
    resourceType: resource?.type,
  })

  const handleChangeThread = useCallback((threadId: string | null) => {
    setActiveThreadId(threadId)
    setThreadParam(threadId)
  }, [])

  const { threads } = useThreads({
    provider,
    user,
    onChangeThread: handleChangeThread,
  })

  const activeThread = useMemo(() => {
    if (!threads.length || activeThreadId === null) {
      return null
    }

    return threads
      .filter((t) => Boolean(!t.resolvedAt))
      .find((thread) => thread.id === activeThreadId)
  }, [threads, activeThreadId])

  // URL navigation support for threads
  useThreadNavigation({
    editorRef: editorContainerRef,
    isSynced: provider?.isSynced,
    onOpenThread: handleChangeThread,
  })

  const collaborationEnabled = !!documentId
  const collaborationReady = provider !== undefined
  const debouncedSetSaveState = useMemo(() => debounce(setSaveState, 100), [])

  const handleCollabUpdate = useCallback((data: CollabOnUpdateProps) => {
    setVersions(data.versions)
  }, [])

  const handleDocumentUpdate = useCallback(() => {
    const updatedAt = new Date()
    debouncedSetSaveState({ state: 'saved', lastUpdatedAt: updatedAt })
    onSaveContent?.(updatedAt)
  }, [debouncedSetSaveState, onSaveContent])

  const { connectionStatus, hasChanged, hasDraft, hasUnsyncedChanges } =
    useContentSyncStatus({
      editor,
      provider,
      onUpdateComplete: handleDocumentUpdate,
    })

  const { data: post } = useReadContentById(
    resource?.id || '',
    !!resource?.id && resource?.type === 'content'
  )

  const commentsPending = useMemo(
    () => resource?.type === 'content' && !post?.richTextDocumentId,
    [post, resource]
  )

  const onSave = useCallback(() => {
    if (commentsPending && editor) {
      threads.forEach((thread) => {
        thread.comments.forEach((comment) => {
          if (comment.data.pending) {
            editor.commands.updateComment({
              threadId: thread.id,
              id: comment.id,
              content: comment.content,
              data: { ...comment.data, pending: false, isUserEdit: false },
            })
          }
        })
      })
    }
  }, [editor, commentsPending, threads])

  // Helper to enforce at most one space in mention queries. This helps
  // ensure the mention list popover only appears when expected.
  const allowMentionWithMaxOneSpace = ({
    state,
    range,
  }: {
    state: EditorState
    range: { from: number; to: number }
  }): boolean => {
    const text = state.doc.textBetween(range.from, range.to, '\0', '\0')
    const spaceCount = (text.match(/\s/g) || []).length
    return spaceCount < 2
  }

  usePostSaveCallback({
    radio: getBackboneWindow()?.OpalMoments_m?.PostShroud?.Radio,
    onSave,
  })

  // Set initial save state from provider or most recent version
  useEffect(() => {
    const latestVersion = versions.at(-1)
    const lastUpdatedAt =
      provider?.getLastSaved() ??
      (latestVersion ? new Date(latestVersion.date) : null)

    if (saveState === undefined && lastUpdatedAt !== null) {
      setSaveState({ state: 'saved', lastUpdatedAt })
    }
  }, [provider, versions, saveState])

  // Sets up the collaboration provider
  useEffect(() => {
    if (!documentId) return
    // Don't attempt to set up provider if token hasn't been retrieved yet.
    if (externalAccessToken === PENDING_COLLABORATION_TOKEN) return

    // Ensure the provider object reference doesn't change after init
    setProvider((provider) => {
      let configuredProvider = provider
      const { baseUrl, ...config } = getDefaultProviderConfig(
        documentId,
        externalAccessToken,
        () => configuredProvider?.disconnect()
      )

      const documentChanged =
        provider != null && provider.configuration.name !== documentId
      if (yDoc.current === null || documentChanged) {
        yDoc.current = new YDoc()
      }

      if (!provider) {
        configuredProvider = new TiptapCollabProvider({
          ...config,
          baseUrl,
          document: yDoc.current,
        })
        return configuredProvider
      } else if (documentChanged) {
        // Without websocketProvider, setConfiguration makes a new socket, and
        // disconnect() then stops that one instead of the socket in use.
        provider.setConfiguration({
          ...config,
          document: yDoc.current,
          websocketProvider: provider.configuration.websocketProvider,
        })
      }

      return provider
    })

    return () => debouncedSetSaveState.cancel()
  }, [externalAccessToken, documentId, debouncedSetSaveState])

  // Update name for collaboration caret
  useEffect(() => {
    // updateUser may be undefined if CollaborationCaret is not enabled
    editor?.commands.updateUser?.({
      name: user ? user.name : 'Anonymous',
      color: user ? getCursorColor(user.name) : GemColors.IN300,
    })
  }, [editor, user])

  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        // If Tiptap reports unsynced changes, prompt user before
        // leaving the page
        if (hasUnsyncedChanges) {
          event.preventDefault()
          event.returnValue = true
        }

        // Create a new version if we have draft changes
        if (provider != null && hasChanged && hasDraft) {
          provider.createVersion()
        }
      },
      [provider, hasUnsyncedChanges, hasChanged, hasDraft]
    )
  )

  // Cleanup provider on unload
  useEffect(() => {
    if (provider) {
      return () => provider.destroy()
    }
  }, [provider])

  // Listens for the event rather than reading `isSynced` during render, since
  // nothing else guarantees a render once the provider syncs
  useEffect(() => {
    if (!provider) return
    if (provider.isSynced) {
      setInitialLoadComplete(true)
      return
    }

    const onSynced = () => setInitialLoadComplete(true)
    provider.on('synced', onSynced)
    return () => {
      provider.off('synced', onSynced)
    }
  }, [provider])

  // The REST body only catches up with the collaboration service later, so a
  // cached copy is stale once this session changed the document
  useEffect(() => {
    if (!hasChanged) return
    return () => {
      queryClient.invalidateQueries(queryKeys.document(documentId))
    }
  }, [hasChanged, queryClient, documentId])

  const preSyncDocument =
    initialLoadComplete || isRefetchingPreSyncBody ? null : preSyncBody
  // Nothing may edit the empty, unsynced document behind the placeholder
  const showsPlaceholder = !provider?.isSynced && !initialLoadComplete

  // Tiptap autofocuses only on mount, and a locked editor can't take focus
  const hasLockedEditor = useRef(false)
  useEffect(() => {
    if (showsPlaceholder) {
      hasLockedEditor.current = true
    } else if (autofocus && hasLockedEditor.current && editor) {
      hasLockedEditor.current = false
      editor.commands.focus('start')
    }
  }, [autofocus, editor, showsPlaceholder])

  if (collaborationEnabled && provider === undefined) {
    return null
  }

  return (
    <DocumentProvider id={documentId}>
      <div
        className={classnames('collaboration-text-editor-container', className)}
        ref={editorContainerRef}
      >
        <FullscreenOverlay
          isFullScreen={isFullScreen && !disableFullScreenOverlay}
          onClick={() => {
            if (isFullScreen && onToggleFullScreen) {
              onToggleFullScreen(false)
            }
          }}
        />
        <TextEditor
          css={styles.container({
            showVersionHistoryControlOnHover,
            theme,
            isFullScreen,
          })}
          className="CollaborationTextEditor"
          contentStyle={[styles.content({ isFullScreen, controlBarPlacement })]}
          focusControlBar={handleFocusControlBar}
          onEditorUpdate={setEditor}
          onUploadAssets={handleUploadAssets}
          assetStore={assetStore}
          onExpandAsset={setExpandedAsset}
          onEscape={() => onToggleFullScreen && onToggleFullScreen(false)}
          autofocus={autofocus === true ? 'start' : false}
          placeholderText={placeholder}
          readOnly={readOnly || showsPlaceholder}
          saveOnChange={{ delay: collaborationReady ? 100 : 500 }}
          overflowHeight={!disableReadMoreOverlay && !isFullScreen ? 510 : null}
          extensions={mergeExtensions({
            aiToolkit: { config: true },
            bubbleMenu: {
              config: { appendTo: selectionMenuAnchor ?? undefined },
            },
            centerJustify: { config: true },
            collaboration: {
              config: collaborationReady
                ? { document: provider.document }
                : false,
            },
            collaborationCaret: {
              config: collaborationReady ? { provider } : false,
            },
            snapshot: {
              config: collaborationReady
                ? { provider, onUpdate: handleCollabUpdate }
                : false,
            },
            undoRedo: { config: !collaborationReady },
            comments: {
              config: collaborationReady
                ? {
                    provider,
                    useLegacyWrapping: false,
                    onClickThread: handleChangeThread,
                  }
                : false,
            },
            mention: {
              config: {
                suggestion: {
                  allowSpaces: true,
                  items: getMentions(resource?.type, resource?.id),
                  render: createMentionListRenderer(),
                  allow: allowMentionWithMaxOneSpace,
                },
              },
            },
          })}
        >
          {controlBarPlacement === 'top' && (
            <FullControlBar
              alwaysVisible={alwaysShowControlBar}
              className="CollaborationTextEditorControlBar"
              documentName={post?.name ?? resource?.name}
              hasError={hasError}
              isFullScreen={isFullScreen}
              onPickAssets={onPickAssets}
              ref={controlBarFocusRef}
              saveState={saveState}
              setFullScreen={
                onToggleFullScreen ? onToggleFullScreen : undefined
              }
              setShowShortcuts={setShowShortcuts}
              disableDoneButton={disableDoneButton}
              disableFullScreenAndCenterControls={
                disableFullScreenAndCenterControls
              }
              disableLastSavedTime={disableLastSavedTime}
              disableViewModeSelector={readOnly || disableViewModeSelector}
            />
          )}
          <ConnectionBanner
            status={connectionStatus}
            hasUnsyncedChanges={hasUnsyncedChanges}
            style={
              controlBarPlacement === 'top'
                ? { insetBlockStart: controlBarFocusRef.current?.offsetHeight }
                : {
                    borderStartStartRadius: 'inherit',
                    borderStartEndRadius: 'inherit',
                  }
            }
          />
          <AIReviewBanner documentId={documentId} editor={editor} />
          {user && activeThread && (
            <ThreadPopover
              thread={activeThread}
              user={user}
              onClose={() => handleChangeThread(null)}
              commentsPending={commentsPending}
            />
          )}
          <SelectionMenu
            commentsPending={commentsPending}
            resource={resource}
            user={user}
          />
          <AssetModal
            expandedAsset={expandedAsset}
            onClose={() => setExpandedAsset(null)}
          />
          {isFullScreen && onToggleFullScreen && (
            <FullscreenToggle setFullScreen={onToggleFullScreen} />
          )}
          <ReadMoreOverlay
            isFullScreen={disableReadMoreOverlay || isFullScreen}
          />
          <TextEditorContent
            css={styles.contentWrapper(isFullScreen, provider?.isSynced)}
            className="CollaborationTextEditorContent"
          >
            {showsPlaceholder && (
              <PlaceholderDocument body={preSyncDocument} readOnly={readOnly} />
            )}
          </TextEditorContent>
          {collaborationReady && useVersioning && (
            <Fragment>
              <VersioningModal
                provider={provider}
                isOpen={showVersions}
                hasDraft={hasDraft || !versions.length}
                onClose={() => setShowVersions(false)}
                versions={versions}
              />
              <OpenVersionsButton
                alwaysVisible={alwaysShowVersionHistoryControl}
                isFullScreen={isFullScreen}
                onClick={() => setShowVersions(true)}
              />
            </Fragment>
          )}
          {controlBarPlacement === 'bottom' && (
            <FullControlBar
              alwaysVisible={alwaysShowControlBar}
              className="CollaborationTextEditorControlBar"
              documentName={post?.name ?? resource?.name}
              hasError={hasError}
              isFullScreen={isFullScreen}
              onPickAssets={onPickAssets}
              ref={controlBarFocusRef}
              saveState={saveState}
              setFullScreen={
                onToggleFullScreen ? onToggleFullScreen : undefined
              }
              setShowShortcuts={setShowShortcuts}
              disableDoneButton={disableDoneButton}
              disableFullScreenAndCenterControls={
                disableFullScreenAndCenterControls
              }
              disableLastSavedTime={disableLastSavedTime}
              disableViewModeSelector={readOnly || disableViewModeSelector}
            />
          )}
        </TextEditor>
        {showShortcuts && (
          <ShortcutModal
            showShortcuts={showShortcuts}
            setShowShortcuts={setShowShortcuts}
          />
        )}
      </div>
    </DocumentProvider>
  )
}
